import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { Modal, Button, Select, EditableInfoBlock, OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import { useRootStore } from 'store/rootStore';
import { normDateToShortAmericanDate } from '@insurancemenu/imenu-components-lib/dist/helpers/dateFormatters';
import { RateSegmentInfo, RATE_SEGMENT_INFO } from 'global/utils/RateSegmentHelper';
import { useParams, useHistory } from 'react-router-dom';
import { sortByProductLineId } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { createStyles } from './styles';

interface Props {
  show: boolean;
  onClose: () => void;
  openPreviousForm: () => void;
}

const TemplatesModal: React.FC<Props> = observer(({ show, onClose, openPreviousForm }) => {
  const classes = createStyles();

  const [value, setValue] = useState('');
  const [errorWithApply, setErrorWithApply] = useState(false);
  const { groupId } = useParams<{ groupId: string }>();
  const history = useHistory();

  const onTemplateChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setValue(e.target.value);
  };

  const rootStore = useRootStore();
  const {
    templatesModalStore: { loading, rateSegment, getTemplatesOptions, applyBrokerCart, closeTemplatesModal },
    browsingModalsStore: {
      form: {
        fields: { effectiveDate, zipCode, county, state },
      },
      loading: browsingLoading,
    },
  } = rootStore;

  const bc = {
    effectiveDate: effectiveDate.value,
    zipCode: zipCode.value,
    county: county.value,
    state: state.value,
  };

  useEffect(() => {
    getTemplatesOptions();
  }, []);

  async function applyTemplate(e: React.FormEvent): Promise<void> {
    e.preventDefault();

    const { id, cartItems } = await applyBrokerCart(value, bc, groupId);

    const productLineIds = [...new Set(cartItems.map(({ productLineId }) => productLineId).sort(sortByProductLineId))];
    const firstProductLineId = productLineIds[0];

    if (id) {
      switch (rateSegment) {
        case 'CUSTOM':
          break;
        case 'INDIVIDUAL':
          break;

        default:
          history.push(`/browsing?groupId=${groupId}&productLineId=${firstProductLineId}&cartId=${id}`);
          break;
      }
      closeTemplatesModal();
    } else {
      setErrorWithApply(true);
    }
  }

  const templatesOptionsList = getTemplatesOptions()
    .map((template) => toJS(template))
    .slice();

  const ModalHeader = (title: string, subtitle: string | JSX.Element): JSX.Element => {
    return (
      <div>
        <p className={classes.actionBlockTitle}>{title}</p>
        <p className={classes.actionBlockCaption}>{subtitle}</p>
      </div>
    );
  };

  return (
    <Modal show={show} onClose={onClose} className={classes.modalSpacing} id="use-templates">
      <div className={classes.body}>
        <h3 className={classes.title}>{`Choose Proposal: ${
          RATE_SEGMENT_INFO[rateSegment as keyof RateSegmentInfo]?.label
        }`}</h3>
        <EditableInfoBlock
          onEditInfo={openPreviousForm}
          title={`Effective date: ${normDateToShortAmericanDate(effectiveDate.value)}, Zip: ${zipCode.value}`}
        />

        <form className={classes.form} onSubmit={applyTemplate} id="templates_form">
          {templatesOptionsList.length === 0 ? (
            ModalHeader(
              `You don't have any proposals saved for this catalog and/or effective date`,
              <span>
                <b>Tip:</b> If you create any new proposal for this catalog, you can save it to your favorites.
              </span>,
            )
          ) : (
            <>
              {ModalHeader(
                'Select a proposal from your favorites in the drop down.',
                <span>
                  <b>Tip:</b> You can customize your selected proposal for each client.
                </span>,
              )}
              <Select
                name="templateName"
                options={templatesOptionsList}
                value={value}
                onChange={onTemplateChange}
                className={classes.formSelect}
              />
            </>
          )}
        </form>
      </div>
      <footer className={classes.footer}>
        <div className={classes.actionsBlock}>
          <Button type="outlined" onClick={onClose} size="large">
            Cancel
          </Button>
          <div className={classes.continueButtonWrapper}>
            <Button color="primary" htmlType="submit" size="large" htmlForm="templates_form" disabled={!value}>
              Continue
            </Button>
            {errorWithApply && <p className={classes.errorMessage}>Error with apply template, please try again.</p>}
          </div>
        </div>
      </footer>
      <OverlaySpinner display={loading || browsingLoading} />
    </Modal>
  );
});

export default TemplatesModal;
